import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { BoFormsModule } from '@bo/ng-forms';
import { AccountingService } from '../../../_services/accounting/accounting.service';
import { AccountingTaxRates } from '../../../../../../_interfaces/accounting.interface';

interface TaxRateOption {
  id: number;
  displayName: string;
}

@Component({
  selector: 'app-tax-rate-select',
  imports: [NgSelectComponent, BoFormsModule],
  templateUrl: './tax-rate-select.component.html',
  standalone: true,
})
export class TaxRateSelectComponent {
  accountingService = inject(AccountingService);

  component = input<string | undefined>(undefined);
  disabled = input<boolean>(false);
  selectedTaxRate = input<AccountingTaxRates | undefined>(undefined);
  preferredTaxRateId = input<number | undefined>(undefined); // Preferred tax rate based on category
  selectedTaxRateChange = output<number | undefined>();
  taxRates = this.accountingService.taxRates;

  taxRateOptions = computed<TaxRateOption[]>(() => {
    return this.taxRates().map((taxRate) => ({
      id: taxRate.id,
      displayName: `${taxRate.name} (${taxRate.rate}%)`,
    }));
  });

  selectedTaxRateId = signal<number | undefined>(undefined);
  private readonly hasInitialized = signal<boolean>(false); // Track if initial load is complete
  private readonly lastPreferredTaxRateId = signal<number | undefined>(undefined); // Track the last preferred ID we processed

  /**
   * Find the default tax rate - prioritises "Standard Rate Purchases"
   * Falls back to first item if not found
   */
  private findDefaultTaxRate(taxRates: AccountingTaxRates[]): number | undefined {
    if (taxRates.length === 0) return undefined;

    // Priority 1: Find "Standard Rate Purchases" (case-insensitive)
    const standardRateTaxRate = taxRates.find((rate) => rate.name.toLowerCase().includes('standard rate purchases'));
    if (standardRateTaxRate) return standardRateTaxRate.id;

    // Priority 2: Use first item
    return taxRates[0].id;
  }

  constructor() {
    // Handle default selection, saved data, and preferred tax rate
    effect(() => {
      const taxRate = this.selectedTaxRate();
      const preferredId = this.preferredTaxRateId();
      const availableTaxRates = this.taxRates();
      const currentSelection = this.selectedTaxRateId();
      const initialized = this.hasInitialized();
      const lastPreferredId = this.lastPreferredTaxRateId();

      if (!initialized) {
        this.handleInitialLoad(taxRate, preferredId, availableTaxRates);
        return;
      }

      this.handlePreferredIdChange(preferredId, lastPreferredId, availableTaxRates, currentSelection);
    });
  }

  private handleInitialLoad(
    taxRate: AccountingTaxRates | undefined,
    preferredId: number | undefined,
    availableTaxRates: AccountingTaxRates[],
  ): void {
    // Priority 1: Use saved tax rate from API
    if (this.applySavedTaxRate(taxRate, preferredId)) {
      return;
    }

    // Priority 2: Use preferred tax rate from category
    this.applyPreferredTaxRate(preferredId, availableTaxRates, true);
  }

  private applySavedTaxRate(taxRate: AccountingTaxRates | undefined, preferredId: number | undefined): boolean {
    if (!taxRate?.id) {
      return false;
    }

    this.selectedTaxRateId.set(taxRate.id);
    this.hasInitialized.set(true);
    this.lastPreferredTaxRateId.set(preferredId);
    return true;
  }

  private applyPreferredTaxRate(
    preferredId: number | undefined,
    availableTaxRates: AccountingTaxRates[],
    isInitialLoad: boolean,
  ): boolean {
    if (preferredId === undefined || preferredId === null) {
      return false;
    }

    const preferredExists = availableTaxRates.some((rate) => rate.id === preferredId);
    if (!preferredExists) {
      return false;
    }

    this.selectedTaxRateId.set(preferredId);
    this.selectedTaxRateChange.emit(preferredId);

    if (isInitialLoad) {
      this.hasInitialized.set(true);
      this.lastPreferredTaxRateId.set(preferredId);
    }

    return true;
  }

  private handlePreferredIdChange(
    preferredId: number | undefined,
    lastPreferredId: number | undefined,
    availableTaxRates: AccountingTaxRates[],
    currentSelection: number | undefined,
  ): void {
    if (preferredId === lastPreferredId) {
      return;
    }

    // Try to apply the new preferred ID
    const applied = this.applyPreferredTaxRate(preferredId, availableTaxRates, false);

    // If preferred is null/undefined and no selection exists, set default
    if (!applied && currentSelection === undefined && availableTaxRates.length > 0) {
      this.applyDefaultTaxRate(availableTaxRates);
    }

    this.lastPreferredTaxRateId.set(preferredId);
  }

  private applyDefaultTaxRate(availableTaxRates: AccountingTaxRates[]): void {
    const defaultId = this.findDefaultTaxRate(availableTaxRates);
    if (defaultId !== undefined) {
      this.selectedTaxRateId.set(defaultId);
      this.selectedTaxRateChange.emit(defaultId);
    }
  }

  updateTaxRate(taxRateId: number | undefined) {
    // User manually changed - this takes priority until preferredId changes again (LAST action wins)
    this.selectedTaxRateId.set(taxRateId);
    this.selectedTaxRateChange.emit(taxRateId);
    // Don't update lastPreferredTaxRateId - this allows future category changes to override
  }
}
